import { useEffect, useState, useCallback } from "react";
import api, { API, setPageMeta } from "@/lib/api";
import { StatusBadge, shortId, ORDER_LABELS, QUOTE_LABELS } from "@/lib/labels";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil, Trash2, Download, Paperclip, Package, FileText, Users, Boxes, Euro, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", category: "objets-personnalises", description: "", price: "", material: "PLA", stock: 0, featured: false, active: true, images: "", colors: "", dimensions: "", variants: "" };

function toForm(p) {
  return {
    ...EMPTY, ...p, price: String(p.price ?? ""),
    images: (p.images || []).join("\n"),
    colors: (p.colors || []).join(", "),
    dimensions: (p.dimensions || []).join(", "),
    variants: (p.variants || []).map((v) => `${v.name}|${v.price_delta}`).join("\n"),
  };
}
function toPayload(f) {
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
  return {
    name: f.name, category: f.category, description: f.description,
    price: parseFloat(f.price) || 0, material: f.material, stock: parseInt(f.stock) || 0,
    featured: !!f.featured, active: !!f.active,
    images: lines(f.images), colors: csv(f.colors), dimensions: csv(f.dimensions),
    variants: lines(f.variants).map((l) => { const [name, d] = l.split("|"); return { name: name.trim(), price_delta: parseFloat(d) || 0 }; }),
  };
}

function downloadFile(fileId, name) {
  const token = localStorage.getItem("tap_token");
  fetch(`${API}/files/${fileId}/download`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.blob()).then((blob) => {
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
    });
}

function QuoteAmount({ quote, onSaved }) {
  const [val, setVal] = useState(quote.devis_amount ? String(quote.devis_amount) : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const amount = parseFloat(val);
    if (isNaN(amount) || amount < 0) { toast.error("Montant invalide"); return; }
    setSaving(true);
    try {
      await api.patch(`/admin/quotes/${quote.id}/amount`, { amount });
      toast.success("Montant du devis envoyé au client");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-end gap-2">
      <div>
        <Label className="text-xs">Montant du devis (€)</Label>
        <Input data-testid={`quote-amount-input-${quote.id}`} type="number" step="0.01" value={val}
          onChange={(e) => setVal(e.target.value)} className="bg-black border-white/15 mt-1 w-40" placeholder="0.00" />
      </div>
      <Button data-testid={`quote-amount-save-${quote.id}`} onClick={save} disabled={saving} className="btn-blue rounded-full font-bold">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le devis"}
      </Button>
      {quote.payment_status === "paid" && <span className="text-sm font-bold text-green-400 pb-2">✓ Réglé</span>}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5">
      <Icon className="w-6 h-6" style={{ color }} />
      <p className="mt-3 text-2xl font-black">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

function ProductDialog({ cats, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial ? toForm(initial) : EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (initial) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(initial ? "Produit mis à jour" : "Produit créé");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(initial ? toForm(initial) : EMPTY); }}>
      <DialogTrigger asChild>
        {initial
          ? <Button variant="ghost" size="sm" data-testid={`edit-product-${initial.id}`}><Pencil className="w-4 h-4" /></Button>
          : <Button className="btn-primary rounded-full font-bold" data-testid="add-product-btn"><Plus className="w-4 h-4 mr-1" /> Nouveau produit</Button>}
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-[#222] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{initial ? "Modifier le produit" : "Nouveau produit"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Nom</Label><Input data-testid="pf-name" value={form.name} onChange={set("name")} className="bg-black border-white/15 mt-1" /></div>
            <div><Label>Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="pf-category" className="bg-black border-white/15 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.filter((c) => c.slug !== "demande-de-devis").map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prix (€)</Label><Input data-testid="pf-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="bg-black border-white/15 mt-1" /></div>
            <div><Label>Matière</Label><Input value={form.material} onChange={set("material")} className="bg-black border-white/15 mt-1" /></div>
            <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={set("stock")} className="bg-black border-white/15 mt-1" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="pf-description" value={form.description} onChange={set("description")} className="bg-black border-white/15 mt-1" /></div>
          <div><Label>Images (une URL par ligne)</Label><Textarea value={form.images} onChange={set("images")} rows={2} className="bg-black border-white/15 mt-1" placeholder="/images/mon-produit.jpeg" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Couleurs (séparées par virgule)</Label><Input value={form.colors} onChange={set("colors")} className="bg-black border-white/15 mt-1" placeholder="Fuchsia, Bleu, Noir" /></div>
            <div><Label>Dimensions (virgule)</Label><Input value={form.dimensions} onChange={set("dimensions")} className="bg-black border-white/15 mt-1" placeholder="10 cm, 15 cm" /></div>
          </div>
          <div><Label>Variantes (Nom|supplément € par ligne)</Label><Textarea value={form.variants} onChange={set("variants")} rows={2} className="bg-black border-white/15 mt-1" placeholder="Grand modèle|12" /></div>
          <div className="flex gap-8">
            <label className="flex items-center gap-2"><Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} /> <span>Mis en avant</span></label>
            <label className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} /> <span>Actif</span></label>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="pf-save" onClick={save} disabled={saving} className="btn-primary rounded-full font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);

  const loadAll = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/products", { params: { include_inactive: true } }).then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
    api.get("/admin/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPageMeta("Administration — Tenay Ain-Pression");
    api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
    loadAll();
  }, [loadAll]);

  const delProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`); toast.success("Produit supprimé"); loadAll();
  };
  const setOrderStatus = async (id, status) => {
    await api.patch(`/admin/orders/${id}/status`, { status }); toast.success("Statut mis à jour"); loadAll();
  };
  const setQuoteStatus = async (id, status) => {
    await api.patch(`/admin/quotes/${id}/status`, { status }); toast.success("Statut mis à jour"); loadAll();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-heading text-4xl font-black">Espace administrateur</h1>
      <p className="mt-2 text-gray-400">Gérez vos produits, commandes, devis et clients.</p>

      {stats && (
        <div className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-6">
          <StatCard icon={Euro} label="Chiffre payé" value={`${stats.revenue.toFixed(2)} €`} color="#E6007E" />
          <StatCard icon={ShoppingBag} label="Commandes" value={stats.orders} color="#0055FF" />
          <StatCard icon={Package} label="Payées" value={stats.paid_orders} color="#E6007E" />
          <StatCard icon={FileText} label="Devis" value={stats.quotes} color="#0055FF" />
          <StatCard icon={Users} label="Clients" value={stats.customers} color="#E6007E" />
          <StatCard icon={Boxes} label="Produits" value={stats.products} color="#0055FF" />
        </div>
      )}

      <Tabs defaultValue="products" className="mt-10">
        <TabsList className="bg-[#0A0A0A] border border-[#222] flex-wrap h-auto">
          <TabsTrigger value="products" data-testid="admin-tab-products">Produits</TabsTrigger>
          <TabsTrigger value="orders" data-testid="admin-tab-orders">Commandes</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Devis</TabsTrigger>
          <TabsTrigger value="customers" data-testid="admin-tab-customers">Clients</TabsTrigger>
        </TabsList>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4"><ProductDialog cats={cats} onSaved={loadAll} /></div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-4 flex gap-4">
                <img src={(p.images || [])[0] || "/images/custom_object.jpeg"} alt="" className="w-20 h-20 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.name}</p>
                  <p className="text-sm text-[#E6007E] font-black">{p.price.toFixed(2)} €</p>
                  <p className="text-xs text-gray-500">{p.category}{!p.active && " · inactif"}{p.featured && " · ⭐"}</p>
                  <div className="mt-2 flex gap-1">
                    <ProductDialog cats={cats} initial={p} onSaved={loadAll} />
                    <Button variant="ghost" size="sm" onClick={() => delProduct(p.id)} data-testid={`del-product-${p.id}`} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* ORDERS */}
        <TabsContent value="orders" className="mt-6 space-y-4">
          {orders.length === 0 ? <p className="text-gray-400">Aucune commande.</p> : orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="font-heading font-bold">#{shortId(o.id)} — {o.shipping_name}</p>
                  <p className="text-sm text-gray-500">{o.user_email} · {new Date(o.created_at).toLocaleString("fr-FR")}</p>
                  <p className="text-sm text-gray-400 mt-1">{o.shipping_address}, {o.shipping_zip} {o.shipping_city} · {o.phone}</p>
                </div>
                <div className="text-right">
                  <p className="font-black text-[#E6007E] text-lg">{o.total.toFixed(2)} €</p>
                  <p className="text-xs text-gray-500">Paiement : {o.payment_status}</p>
                </div>
              </div>
              <div className="mt-3 text-sm text-gray-300">
                {o.items.map((it, i) => <div key={i}>{it.name} ×{it.quantity} {it.color && `· ${it.color}`} {it.variant && `· ${it.variant}`} — {it.line_total.toFixed(2)} €</div>)}
              </div>
              {o.files?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{o.files.map((f) => (
                <button key={f.id} onClick={() => downloadFile(f.id, f.original_filename)} className="inline-flex items-center gap-1.5 rounded-full border border-[#0055FF]/40 bg-[#0055FF]/10 px-3 py-1 text-xs hover:border-[#0055FF]"><Paperclip className="w-3 h-3" /> {f.original_filename} <Download className="w-3 h-3" /></button>
              ))}</div>}
              <div className="mt-4 flex items-center gap-3">
                <StatusBadge status={o.status} type="order" />
                <Select value={o.status} onValueChange={(v) => setOrderStatus(o.id, v)}>
                  <SelectTrigger data-testid={`order-status-${o.id}`} className="bg-black border-white/15 w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ORDER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* QUOTES */}
        <TabsContent value="quotes" className="mt-6 space-y-4">
          {quotes.length === 0 ? <p className="text-gray-400">Aucun devis.</p> : quotes.map((q) => (
            <div key={q.id} data-testid={`admin-quote-${q.id}`} className="rounded-2xl border border-[#222] bg-[#0A0A0A] p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="font-heading font-bold">{q.subject}</p>
                  <p className="text-sm text-gray-500">{q.user_email} · {q.category} · qté {q.quantity} · {new Date(q.created_at).toLocaleString("fr-FR")}</p>
                  {q.phone && <p className="text-sm text-gray-400">Tél : {q.phone}</p>}
                </div>
                <StatusBadge status={q.status} type="quote" />
              </div>
              <p className="mt-3 text-sm text-gray-300">{q.description}</p>
              {q.files?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{q.files.map((f) => (
                <button key={f.id} onClick={() => downloadFile(f.id, f.original_filename)} className="inline-flex items-center gap-1.5 rounded-full border border-[#0055FF]/40 bg-[#0055FF]/10 px-3 py-1 text-xs hover:border-[#0055FF]"><Paperclip className="w-3 h-3" /> {f.original_filename} <Download className="w-3 h-3" /></button>
              ))}</div>}
              <div className="mt-4 flex flex-wrap items-center gap-4">
                <Select value={q.status} onValueChange={(v) => setQuoteStatus(q.id, v)}>
                  <SelectTrigger data-testid={`quote-status-${q.id}`} className="bg-black border-white/15 w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(QUOTE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <QuoteAmount quote={q} onSaved={loadAll} />
              </div>
            </div>
          ))}
        </TabsContent>

        {/* CUSTOMERS */}
        <TabsContent value="customers" className="mt-6">
          {customers.length === 0 ? <p className="text-gray-400">Aucun client.</p> : (
            <div className="rounded-2xl border border-[#222] bg-[#0A0A0A] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-black/50 text-gray-400"><tr><th className="text-left p-4">Nom</th><th className="text-left p-4">E-mail</th><th className="text-left p-4">Téléphone</th><th className="text-left p-4">Inscrit le</th></tr></thead>
                <tbody>{customers.map((c) => (
                  <tr key={c.id} className="border-t border-[#222]"><td className="p-4 font-semibold">{c.name}</td><td className="p-4">{c.email}</td><td className="p-4">{c.phone || "—"}</td><td className="p-4 text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString("fr-FR") : "—"}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
